<template>
  <owal-modal-panel
    :title="title"
    :bottom-buttons="buttons"
    presentation-style="form"
    @button-click="handleButton"
    >
    <owal-form
      ref="form"
      :form="inputForm"
      :initial-submission="initialSubmission"
      :embedded="true"
      @change="updateButtons"
      @submit-end="handleSubmit"
      :metadata="metadata"
      :submittingOverride="submitInProgress"
      @submit-start="submitIdentity"
      />
  </owal-modal-panel>
</template>

<script>
import OwalForm from '@/libraries/owal-ui/forms/OwalForm'
import OwalModalPanel from '@/libraries/owal-ui/panels/OwalModalPanel'
import { addCredential } from '@/libraries/user-management'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'EditIdentityPanel',
  components: { OwalForm, OwalModalPanel },
  props: {
    identityToEdit: {
      type: Object,
      default: () => {},
      required: false,
    },
    initialImage: {
      default: undefined,
      required: false,
    },
  },
  data () {
    return {
      readyToSubmit: false,
      inputForm: addCredential,
    }
  },
  computed: {
    ...mapGetters({
      currentDomainUUID: 'currentDomainUUID',
      submitInProgress: 'credentialMismatch/submitInProgress',
    }),
    metadata () {
      return {
        domain_uuid: this.currentDomainUUID,
      }
    },
    title () {
      return this.identityToEdit ? 'Edit Identity' : 'Add Identity'
    },
    endpoint () {
      let endpoint = '/api/api/api/v8/faces'
      if (this.identityToEdit) {
        const uuid = this.identityToEdit.frontend_face_uuid
        if (uuid) {
          endpoint = `${endpoint}/${uuid}`
        }
      }
      return endpoint
    },
    initialSubmission () {
      let identity = Object.assign({}, this.identityToEdit)

      if (this.initialImage) {
        identity.image = this.initialImage
      }

      if (identity.role) {
        identity.tor = this.identityToEdit.role.toLowerCase() === 'tor'
      }

      if (identity.name && !identity.first_name) {
        const split = this.identityToEdit.name.split(/\s+/)
        const parts = split.filter(part => part !== '')
        if (parts.length < 2) {
          identity.first_name = parts[0]
        } else if (parts.length < 3) {
          identity.first_name = parts[0]
          identity.last_name = parts[1]
        } else if (parts.length < 4) {
          identity.first_name = `${parts[0]} ${parts[1]}`
          identity.last_name = parts[2]
        } else {
          identity.first_name = `${parts[0]} ${parts[1]}`
          identity.last_name = parts.reduce((lastName, currentPart, index) => {
            if (index > 1) {
              lastName += `${currentPart} `
              return lastName
            } else return ''
          }, '')
        }
      }

      if (identity.credentials) {
        const credentialIds = identity.credentials.map(credential => {
          return {
            number: credential.credential_number,
            uuid: credential.credential_uuid,
          }
        })
        identity.credentials = credentialIds
      }

      const submission = this.inputForm.initializationTemplate.processSubmission(identity)
      return submission
    },
    buttons: function () {
      let buttons = [{
        title: 'cancel',
        identifier: 'cancel',
      }]

      if (this.readyToSubmit) {
        buttons.push({
          title: 'Save',
          identifier: 'save',
        })
      }

      return buttons
    },
  },
  methods: {
    ...mapActions({
      editIdentityProfile: 'credentialMismatch/editIdentityProfile',
      createNewIdentityProfile: 'credentialMismatch/createNewIdentityProfile',
    }),
    handleSubmit: function ({ submission, endpoint, response, success, error }) {
      let uuid = ''
      if (response && response.data) {
        uuid = response.data.frontend_face_uuid
      }
      this.closeWindow(success, uuid)
    },
    handleButton (identifier) {
      if (identifier === 'cancel') {
        this.closeWindow(false)
      } else if (identifier === 'save') {
        this.$refs.form.submit()
      }
    },
    closeWindow (didSubmit, result) {
      this.$emit('close')
    },
    updateButtons: function (submission) {
      this.readyToSubmit = this.inputForm.validateSubmission(submission)
    },
    submitIdentity (form) {
      this.submitting = true
      // build the submission object
      const identityForSubmission = Object.assign({}, form.identity_info, { domain_uuid: form.domain_uuid })

      const images = !!(this.initialImage && identityForSubmission.image) // is there both an initial image and a new image
      const sameImage = images && !!(identityForSubmission.image.data_b64 === identityForSubmission.image.i_b64)
      if (images && sameImage) {
        // the images are the same, keep the previous S3 URL
        identityForSubmission.image = this.identityToEdit.imageURL
      } else if (images && this.identityToEdit.imageURL) {
        // the images are different, use new b64 and delete old from s3
        identityForSubmission.image = identityForSubmission.image.data_b64
        identityForSubmission.image_delete_key = this.identityToEdit.imageURL.match(/keycard\/dom-.*/)[0]
      } else if (identityForSubmission.image) {
        // there is a new image and no previous image
        identityForSubmission.image = identityForSubmission.image.data_b64
      } else if (this.initialImage && !images) {
        // there is a pevious image and no new image, delete old from s3
        identityForSubmission.image_delete_key = this.identityToEdit.imageURL.match(/keycard\/dom-.*/)[0]
      }

      // if editing and editing profile has >= 1 credentials
      if (this.identityToEdit && this.identityToEdit.credentials && this.identityToEdit.credentials.length > 0) {
        identityForSubmission.credentials = identityForSubmission.credentials.map(function (credential, index) {
          let newCredential
          if (credential.length < 1) {
            const reasonForDeactivation = window.prompt('Please enter a reason for deactivating this card.', 'lost, stolen')
            const deactivatedFor = reasonForDeactivation.split(',').map(reason => reason.replace(/(^\s)|,|(\s$)/g, ''))
            newCredential = { deactivatedFor }
          } else {
            newCredential = { 'number': credential }
          }

          if (this.identityToEdit.credentials[index]) {
            newCredential.uuid = this.identityToEdit.credentials[index].credential_uuid
          }
          return newCredential
        }.bind(this))
      } else {
        identityForSubmission.credentials = identityForSubmission.credentials.map(credential => {
          return {
            number: credential,
          }
        })
      }

      if (this.identityToEdit) {
        identityForSubmission.identityId = this.identityToEdit.identityId
        this.editIdentityProfile(identityForSubmission)
      } else {
        this.createNewIdentityProfile(identityForSubmission)
      }
    },
  },
  watch: {
    submitInProgress: function (isSubmitting, wasSubmitting) {
      if (wasSubmitting && !isSubmitting) {
        this.closeWindow()
      }
    },
  },
}
</script>

<style scoped>

</style>
